import {Component,Inject,OnInit} from '@angular/core';
import {MAT_DIALOG_DATA,MatDialogConfig,MatDialogRef} from '@angular/material/dialog';
import {WebClient} from '@injectables';
import {op, Subscriptions} from '@lib/rxjs';
import { WhereTable } from '@lib/where';
import { Client, JointUserClient, Site, User } from '@models';

interface Input{
	record:User;
}

@Component({
	selector: 'user-edit-dialog',
	templateUrl: './user-edit-dialog.component.html',
	styleUrls: ['./user-edit-dialog.component.scss']
})
export class UserEditDialog implements OnInit{
	static readonly config:MatDialogConfig<Input>={
		autoFocus: false,
		disableClose: true,
	};
	static readonly output:boolean;

	constructor(
		public readonly user:User,
		private readonly webClient:WebClient,
		private host:MatDialogRef<boolean>,
		@Inject(MAT_DIALOG_DATA) private readonly input: Input,
	) { }

	public readonly clients$ = this.webClient.model.client.search$({})
		.pipe(
			op.tap(() => this.loadingClients = false),
			op.startWith([])
		);
	public readonly initialClient$=this.clients$
		.pipe(op.map(clients=>clients.find(client=>client.id===this.record.clientId)));
	
	//load sites for dropdown
	public siteOptions: Site[] = [];
	public readonly initialSite$ = this.webClient.model.site
	.search$({ where: { clientId: this.input.record.clientId } })
	.pipe(
		op.map((sites) => {
			if (this.user.permissions === 1) {
				return sites.filter((site) => site.id === this.user.siteId);
			}
			return sites;
		}),
		op.tap(() => this.loadingSites = false),
		op.startWith([] as Site[])
  );

	private subscriptions = new Subscriptions();
	public record=this.input.record;
	public client:Client;
	public siteId=this.record.siteId;
	public email=this.record.email ?? '';
	public permissions=this.record.permissions;
	public firstName=this.record.firstName ?? '';
	public lastName=this.record.lastName ?? '';
	public password='';
	public selectedClientIds: number[] = [];
	public isMultiClientMode = false;
	private allSites: Site[] = [];
	public loadingClients = true;
	public loadingSites = false;

	public ngOnInit(){
		this.subscriptions.add(this.initialClient$
			.subscribe(client => {
				this.client = client;
			}));

		this.subscriptions.add(this.initialSite$
			.subscribe((sites) => {
				this.allSites = sites;
				this.filterSites();
			}));

		if (this.record.id) {
			this.subscriptions.add(this.webClient.model.jointUserClient
				.search$({ where: { userId: this.record.id } })
				.subscribe(rows => {
					this.selectedClientIds = rows.map(r => r.clientId);
					this.filterSites();
				}));
		}

		this.updateDropdownMode();
	}

	private filterSites() {
		if (this.selectedClientIds?.length > 0 && this.allSites?.length > 0) {
			this.siteOptions = this.allSites.filter(site =>
				this.selectedClientIds.includes(site.clientId)
			);
		} else {
			this.siteOptions = this.allSites ?? [];
		}
	}

	public onPermissionsChanged(): void {
		this.updateDropdownMode();
		this.client = null;
		this.selectedClientIds = [];
		this.siteId = null;
		this.siteOptions = [];
	}

	private updateDropdownMode(): void {
		this.isMultiClientMode = this.permissions === 3;
	}

	public clientDisplayWith(client: Client) {
		return client?.name ?? '';
	}

	public onClientSelected(client: Client): void {
		this.client = client;
		this.selectedClientIds = [client.id];
		this.siteId = null;
		// load sites for the selected client
		let where: WhereTable = {};
		if (client.id) {
			where['clientId'] = [['in', [client.id]]];
		}
		this.loadSites({ clientId: [['in', [client.id]]] });
	}

	public onMultiClientSelectionChange(selectedIds: number[]): void {
		this.selectedClientIds = selectedIds;
		this.siteId = null;

		if (selectedIds.length > 0) {
			this.subscriptions.add(this.clients$
				.pipe(op.map(clients => clients.find(client => client.id === selectedIds[0]))).subscribe((client) => {
					this.client = client
				}));

			let where: WhereTable = {};
			if (Array.isArray(selectedIds) && selectedIds.length > 0) {
				where['clientId'] = [['in', selectedIds]];
			}
			this.loadSites(where);
		} else {
			this.siteOptions = [];
		}
	}

	private loadSites(where: WhereTable) {
		this.loadingSites = true;

		const sub = this.webClient.model.site
			.search$({ where, sort: { name: '+' } })
			.subscribe((sites) => {
				if (this.user.permissions === 1) {
					this.siteOptions = sites.filter((site) => site.id === this.user.siteId);
				} else {
					this.siteOptions = sites;
				}
				this.loadingSites = false;
			});

		this.subscriptions.add(sub);
	}

	public valid() {
		if (!this.client) return false;
		if (!this.siteId) return false;
		if (!this.email.match(/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/)) return false;
		if (!(this.user.id || this.password)) return false;
		return true;
	}

	public cancel() {
		this.host.close(false);
	}

	public async save() {
		this.record.clientId = this.client.id;
		this.record.siteId = this.siteId;
		this.record.email = this.email.toLowerCase();
		this.record.permissions = this.permissions;
		this.record.firstName = this.firstName;
		this.record.lastName = this.lastName;
		if (this.password)
			this.record.password = this.password;
		await this.webClient.model.user.save(this.record);
		await this._syncJointUserClients();
		this.host.close(true);
	}

	private async _syncJointUserClients() {
		if (!this.record.id) return;

		this.webClient.model.jointUserClient
			.search$({ where: { userId: this.record.id } })
			.subscribe(existingRows => {

				const existingClientIds = existingRows?.map(r => r.clientId) ?? [];
				const toDelete = existingRows?.filter(row => !this.selectedClientIds.includes(row.clientId)) ?? [];
				const toAdd = this.selectedClientIds.filter(id => !existingClientIds.includes(id)) ?? [];
				// perform inserts
				toAdd.forEach(async clientId => {
					const jointUserClient = new JointUserClient();
					jointUserClient.userId = this.record.id
					jointUserClient.clientId = clientId
					await this.webClient.model.jointUserClient.save(jointUserClient)
				});

				// perform deletes
				toDelete.forEach(async client => {
					await this.webClient.model.jointUserClient.delete(client);
				});
			});
	}

	public ngOnDestroy(): void {
		this.subscriptions.unsubscribe();
	}
	
}